// SEQ-240: the content model behind every "How is this worked out" pop-up.
//
// Pure types and labels only (no React, no supabase), so the definition
// modules beside each hook and the Deno tests can import it.
//
// A definition DESCRIBES a figure. It never changes which projects or months a
// figure counts: that stays in the hook that computes it, and each definition
// module names the exact lines it describes so a reviewer editing the hook sees
// the text that has to change with it.

import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";

/** Where a figure's numbers come from. Rendered through SOURCE_KIND_LABELS. */
export type MetricSourceKind = "typed" | "calculated" | "payroll" | "mixed";

export const SOURCE_KIND_LABELS: Record<MetricSourceKind, string> = {
  typed: "Typed in",
  calculated: "Calculated in the app",
  payroll: "From payroll (KeyPay)",
  mixed: "More than one source",
};

/**
 * Which projects a figure counts.
 *
 * - `active-only`: the hook fetches active projects only (status active or
 *   blank). The project and builder filters can narrow it; the status filter
 *   can never add a completed or on hold project.
 * - `active-ignores-filters`: active projects only, and the figure does not
 *   narrow by the project, builder or status filter at all.
 * - `filters`: follows the project, builder and status filters (the status
 *   filter starts on Active).
 * - `all-unless-filtered`: every project the viewer can see, every status,
 *   unless the viewer filters (P&L Analytics).
 */
export type ProjectScope = "active-only" | "active-ignores-filters" | "filters" | "all-unless-filtered";

export const PROJECT_SCOPE_LABELS: Record<ProjectScope, string> = {
  "active-only": "Active projects only. The project and builder filters narrow it, but choosing Completed or All in the status filter cannot add other projects.",
  "active-ignores-filters": "Active projects only. It does not change with the project, builder or status filters.",
  filters: "Follows the project, builder and status filters. The status filter starts on Active.",
  "all-unless-filtered": "All projects you can see, whatever their status, unless you filter.",
};

/**
 * Which dates a figure counts.
 *
 * - `date-filter`: follows the dashboard date filter.
 * - `all-time`: fixed to all time; the date filter does not apply.
 * - `own-window`: the figure has its own range (the detail says which).
 * - `calendar`: fixed calendar months worked out from today (the detail says which).
 * - `page-range`: follows the P&L Analytics date range.
 */
export type DateScope = "date-filter" | "all-time" | "own-window" | "calendar" | "page-range";

export const DATE_SCOPE_LABELS: Record<DateScope, string> = {
  "date-filter": "Follows the date filter.",
  "all-time": "Fixed: all time. The date filter does not apply.",
  "own-window": "Has its own range.",
  calendar: "Fixed calendar months, counted from today.",
  "page-range": "Follows the date range at the top of the page.",
};

export interface MetricDefinition {
  /** The figure's name as the reader sees it. Labels the dialog. */
  name: string;
  /** One plain sentence. Describes the dialog. */
  meaning: string;
  /** Plain words, one step per line. */
  formula: readonly string[];
  source: { kind: MetricSourceKind; detail: string };
  projects: { scope: ProjectScope; detail?: string };
  dates: { scope: DateScope; detail?: string };
  /** The time span the number covers, in words. */
  period: string;
  /**
   * How fresh the data is, already in words, for example "Payroll data as at
   * 3/11/2026, 6:00:00 am". Set only from a real timestamp the page has
   * (P&L Analytics' payroll refresh time); the row is left out otherwise.
   */
  lastUpdated?: string;
}

/** One labelled value for the "Right now" row, formatted by the shared metric formatter so Safe Mode masks it. */
export interface RightNowValue {
  label: string;
  value: number | null;
  unit: PnlUnit;
}
